// Baconian cipher encoding schemes
export const ENCODING_SCHEMES = {
  CAPITAL_LOWERCASE: {
    name: 'Capital vs Lowercase',
    description: 'Capital letters = A, lowercase letters = B',
    encode: (bit, char) => bit === 'A' ? char.toUpperCase() : char.toLowerCase(),
    decode: (char) => char === char.toUpperCase() ? 'A' : 'B',
    getSymbols: () => ['A', 'B'],
  },
  ITALIC_REGULAR: {
    name: 'Italic vs Regular',
    description: 'Italic text = A, regular text = B',
    encode: (bit, char) => bit === 'A' ? { char, italic: true } : { char, italic: false },
    decode: (obj) => obj.italic ? 'A' : 'B',
    getSymbols: () => ['A', 'B'],
  },
  TWO_LETTER: {
    name: 'Two Letter Pattern',
    description: 'Randomly chosen two letters: first = A, second = B',
    encode: (bit, letters) => bit === 'A' ? letters[0] : letters[1],
    decode: (char, letters) => char === letters[0] ? 'A' : 'B',
    getSymbols: () => {
      // Pick two random distinct uppercase letters
      const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let a = alphabet[Math.floor(Math.random() * alphabet.length)];
      let b;
      do {
        b = alphabet[Math.floor(Math.random() * alphabet.length)];
      } while (b === a);
      return [a, b];
    },
  },
  CONSONANT_VOWEL: {
    name: 'Consonant vs Vowel',
    description: 'Consonant = A, Vowel = B (uses only C/V in each group)',
    encode: (bits) => {
      // For each group of 5 bits, generate a 5-letter string with C/V pattern
      const vowels = 'AEIOU';
      const consonants = 'BCDFGHJKLMNPQRSTVWXYZ';
      return bits.split('').map(bit => bit === 'A'
        ? consonants[Math.floor(Math.random() * consonants.length)]
        : vowels[Math.floor(Math.random() * vowels.length)]
      ).join('');
    },
    decode: (letters) => letters.split('').map(l => 'AEIOU'.includes(l.toUpperCase()) ? 'B' : 'A').join(''),
    getSymbols: () => ['C', 'V'],
  },
};

// Baconian cipher mapping (A-Z)
export const BACONIAN_MAP = {
  'A': 'AAAAA', 'B': 'AAAAB', 'C': 'AAABA', 'D': 'AAABB',
  'E': 'AABAA', 'F': 'AABAB', 'G': 'AABBA', 'H': 'AABBB',
  'I': 'ABAAA', 'J': 'ABAAB', 'K': 'ABABA', 'L': 'ABABB',
  'M': 'ABBAA', 'N': 'ABBAB', 'O': 'ABBBA', 'P': 'ABBBB',
  'Q': 'BAAAA', 'R': 'BAAAB', 'S': 'BAABA', 'T': 'BAABB',
  'U': 'BABAA', 'V': 'BABAB', 'W': 'BABBA', 'X': 'BABBB',
  'Y': 'BBAAA', 'Z': 'BBAAB'
};

// Reverse mapping for decoding
export const BACONIAN_REVERSE_MAP = Object.entries(BACONIAN_MAP).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

// Generate a random message and encode it using a random scheme
export const generateEncodedMessage = (message) => {
  const schemes = Object.values(ENCODING_SCHEMES);
  const randomScheme = schemes[Math.floor(Math.random() * schemes.length)];

  // Only encode letters, skip others
  const letters = message.toUpperCase().replace(/[^A-Z]/g, '');
  const groups = [];
  for (let i = 0; i < letters.length; i += 1) {
    const char = letters[i];
    const baconianCode = BACONIAN_MAP[char];
    if (!baconianCode) continue;
    groups.push({ char, code: baconianCode });
  }

  // Group into 5s for Baconian
  const encodedGroups = [];
  let patternSymbols = null;
  if (randomScheme.getSymbols) {
    patternSymbols = randomScheme.getSymbols();
  }
  for (let i = 0; i < groups.length; i++) {
    const { code } = groups[i];
    let display;
    if (randomScheme === ENCODING_SCHEMES.CONSONANT_VOWEL) {
      display = randomScheme.encode(code);
    } else if (randomScheme === ENCODING_SCHEMES.ITALIC_REGULAR) {
      display = code.split('').map(bit => randomScheme.encode(bit, groups[i].char));
    } else if (randomScheme === ENCODING_SCHEMES.CAPITAL_LOWERCASE) {
      display = code.split('').map(bit => randomScheme.encode(bit, groups[i].char)).join('');
    } else if (randomScheme === ENCODING_SCHEMES.TWO_LETTER) {
      display = code.split('').map(bit => randomScheme.encode(bit, patternSymbols)).join('');
    } else {
      display = code.split('').map(bit => groups[i].char).join('');
    }
    encodedGroups.push({ display, code });
  }

  return {
    encodedMessage: encodedGroups.map(g => typeof g.display === 'string' ? g.display : (Array.isArray(g.display) ? g.display.map(d => d.char).join('') : '')).join(' '),
    encodedGroups,
    scheme: randomScheme,
    patternSymbols,
  };
};

// Validate a single letter against its encoded group
export const validateLetter = (encodedGroup, inputLetter) => {
  if (!encodedGroup || encodedGroup === ' ') return true;
  const decodedLetter = BACONIAN_REVERSE_MAP[encodedGroup];
  return decodedLetter === inputLetter.toUpperCase();
}; 